import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

export const TwoFactorSection = () => {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/2fa/status").then(({ data }) => setEnabled(data.enabled)).catch(() => setEnabled(false));
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Setup failed"); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackupCodes(data.backup_codes);
      setEnabled(true);
      setSetup(null);
      setCode("");
      toast.success("Two-factor authentication enabled 🔐");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    const c = window.prompt("Enter a current authenticator code (or a backup code) to turn 2FA off:");
    if (!c) return;
    try {
      await api.post("/auth/2fa/disable", { code: c });
      setEnabled(false);
      setBackupCodes(null);
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invalid code"); }
  };

  if (enabled === null) return null;

  return (
    <div className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="twofa-section">
      <div className="flex items-center gap-2">
        <ShieldCheck className={`h-4 w-4 ${enabled ? "text-emerald-400" : "text-neutral-500"}`} />
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Two-step verification</div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${enabled ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-[#27272A] text-neutral-500"}`} data-testid="twofa-status-badge">
          {enabled ? "ON" : "OFF"}
        </span>
      </div>
      <p className="mt-2 text-xs text-neutral-400">
        Add a second lock to your account: after your password, you'll enter a 6-digit code from an authenticator app
        (Google Authenticator, Authy) — with email codes and backup codes as fallback.
      </p>

      {!enabled && !setup && (
        <Button onClick={startSetup} disabled={busy} data-testid="twofa-setup-btn"
          className="mt-4 bg-white text-black hover:bg-neutral-200 rounded-full h-9 px-5 text-xs font-bold">
          <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Set up 2FA
        </Button>
      )}

      {setup && (
        <div className="mt-4 rounded-lg border border-[#27272A] bg-black/30 p-4">
          <div className="text-xs text-neutral-300 font-bold">1. Scan this QR code with Google Authenticator or Authy</div>
          <img src={setup.qr} alt="2FA QR code" className="mt-3 h-40 w-40 rounded-lg bg-white p-2" data-testid="twofa-qr" />
          <div className="mt-2 text-[10px] text-neutral-500">
            Can't scan? Enter this key manually: <span className="font-mono text-neutral-300 select-all">{setup.secret}</span>
          </div>
          <div className="mt-4 text-xs text-neutral-300 font-bold">2. Enter the 6-digit code the app shows</div>
          <div className="mt-2 flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} data-testid="twofa-enable-input"
              className="w-36 bg-[#0A0A0A] border-[#27272A] font-mono text-center tracking-[0.3em]" placeholder="••••••" />
            <Button onClick={enable} disabled={busy || code.length !== 6} data-testid="twofa-enable-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-black rounded-full h-10 px-5 text-xs font-bold">
              Activate
            </Button>
          </div>
        </div>
      )}

      {backupCodes && (
        <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4" data-testid="twofa-backup-codes">
          <div className="text-xs font-bold text-amber-400">⚠️ Save these backup codes now — shown only once</div>
          <p className="text-[10px] text-neutral-400 mt-1">Each works once if you lose your phone.</p>
          <div className="mt-2 grid grid-cols-4 gap-2 font-mono text-xs text-neutral-200">
            {backupCodes.map((c) => <span key={c} className="select-all">{c}</span>)}
          </div>
          <button onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Backup codes copied"); }}
            className="mt-3 text-[10px] text-[#007AFF] underline">Copy all</button>
        </div>
      )}

      {enabled && !backupCodes && (
        <Button onClick={disable} variant="outline" data-testid="twofa-disable-btn"
          className="mt-4 border-[#27272A] text-neutral-400 hover:text-white rounded-full h-9 px-5 text-xs">
          Turn off 2FA
        </Button>
      )}
    </div>
  );
};
